import { useState } from "react";
import { ProSidebar, Menu, MenuItem } from "react-pro-sidebar";
import { Avatar, Box, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "react-pro-sidebar/dist/css/styles.css";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import { alpha } from "@mui/material/styles";
import { normalizeRole } from "../../utils/permissions";
import { useAlertVisuals } from "../../context/AlertVisualContext";
import { primaryModulesForRole } from "../../modules/navigation";
import { buildSharedPath } from "../../hooks/useSharedFilters";

const activeFor = (pathname, to) => {
  const section = to.split("/")[1];
  if (!section) return pathname === to;
  return pathname === to || pathname.startsWith(`/${section}`);
};

const Item = ({ title, to, icon, isCollapsed, badge, onNavigate, bottom = false }) => {
  const location = useLocation();
  const active = activeFor(location.pathname, to);

  return (
    <Tooltip title={isCollapsed ? title : ""} placement="right">
      <MenuItem className={bottom ? "navigation-bottom" : undefined} active={active} icon={icon}>
        {!isCollapsed && (
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
            <Typography>{title}</Typography>
            {badge ? <Typography variant="caption" color="error.main" fontWeight={600} sx={{ px: 1.25, py: 0.25, borderRadius: 99, border: "1px solid", borderColor: "error.main", bgcolor: "rgba(239,68,68,0.12)", fontFamily: (theme) => theme.custom.monoFont }}>{badge}</Typography> : active ? <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "primary.main" }} /> : null}
          </Box>
        )}
        <Link to={buildSharedPath(to, location.search)} onClick={onNavigate} aria-label={title} />
      </MenuItem>
    </Tooltip>
  );
};

const Sidebar = ({ user, role, compactAllowed = true, onNavigate }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [collapsedPreference, setIsCollapsed] = useState(false);
  const isCollapsed = compactAllowed && collapsedPreference;
  const normalizedRole = normalizeRole(role);
  const { unseenCount: criticalCount } = useAlertVisuals();

  const visibleModules = primaryModulesForRole(normalizedRole)
    .map((module) => module.key === "system" && normalizedRole === "auditor" ? { ...module, to: "/system/health" } : module);

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        width: isCollapsed ? 80 : 320,
        minWidth: isCollapsed ? 80 : 320,
        "& .pro-sidebar": {
          minWidth: "100%",
          width: "100%",
          flex: "0 0 auto",
          height: "auto",
        },
        "& .pro-sidebar-inner": {
          minHeight: 0,
          background: `${theme.custom.glass.background} !important`,
          border: theme.custom.glass.border,
          borderRadius: theme.custom.navigationRadius,
          boxShadow: theme.custom.glass.boxShadow,
          paddingTop: "12px",
        },
        "& .pro-icon-wrapper": { backgroundColor: "transparent !important" },
        "& .pro-sidebar-layout, & .pro-menu, & .pro-menu > ul, & .navigation-items": { height: "auto" },
        "& .navigation-items": { display: "flex", flexDirection: "column", paddingBottom: 1 },
        "& .pro-inner-item": {
          mx: isCollapsed ? "8px" : "12px",
          my: "6px",
          px: isCollapsed ? "8px !important" : "12px !important",
          py: "13px !important",
          borderRadius: "8px",
          color: `${theme.palette.text.secondary} !important`,
          transition: "all 180ms ease",
          border: "1px solid transparent",
        },
        "& .pro-inner-item:hover": {
          color: `${theme.palette.primary.main} !important`,
          background: `${theme.palette.action.hover} !important`,
        },
        "& .pro-menu-item.active .pro-inner-item": {
          color: `${theme.palette.primary.main} !important`,
          background: `${theme.palette.mode === "dark" ? `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.19)}, ${alpha(theme.palette.info.main, 0.07)})` : theme.palette.action.selected} !important`,
          borderColor: theme.palette.primary.main,
          borderLeft: `3px solid ${theme.palette.primary.main}`,
          boxShadow: theme.palette.mode === "dark" ? theme.custom.softGlow : "none",
        },
      }}
    >
      <Box sx={{ p: isCollapsed ? 1.25 : 2.5, background: theme.custom.surface.overlay, border: theme.custom.glass.border, borderRadius: theme.custom.navigationRadius, boxShadow: theme.custom.glass.boxShadow }}>
        <Box display="flex" alignItems="center" gap={1.75}>
          <Box sx={{ width: 48, height: 48, flexShrink: 0, display: "grid", placeItems: "center", border: "1px solid", borderColor: "primary.main", borderRadius: 1, bgcolor: "action.hover", boxShadow: theme.custom.softGlow }}><Box component="img" src={`${process.env.PUBLIC_URL || ""}/iso.png`} alt="Absolluty" sx={{ width: 38, height: 38, objectFit: "contain" }} /></Box>
          {!isCollapsed && <Box flex={1} minWidth={0}>
            <Typography fontSize={20} fontWeight={800} fontFamily={theme.custom.headingFont}>ABSOLLUTY</Typography>
            <Typography variant="caption" color="primary.main">{t("platform.brand.subtitle")}</Typography>
          </Box>}
          {!isCollapsed && compactAllowed && <Tooltip title={t("platform.sidebar.collapse")}><IconButton aria-label={t("platform.sidebar.collapse")} onClick={() => setIsCollapsed(true)} size="small"><MenuOutlinedIcon /></IconButton></Tooltip>}
        </Box>
        {isCollapsed && <IconButton aria-label={t("platform.sidebar.expand")} onClick={() => setIsCollapsed(false)} sx={{ mt: 1 }}><MenuOutlinedIcon /></IconButton>}
        {!isCollapsed && <Box sx={{ mt: 3, pt: 3, borderTop: "1px solid", borderColor: "divider" }}>
          <Box display="flex" alignItems="center" gap={1.5} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1, bgcolor: theme.custom.surface.raised }}>
            <Avatar sx={{ width: 42, height: 42, bgcolor: "action.selected", color: "primary.main", border: "1px solid", borderColor: "primary.main", borderRadius: 1 }}>{(user?.username || user?.email || "U").slice(0, 1).toUpperCase()}</Avatar>
            <Box flex={1} minWidth={0}>
              <Typography variant="caption" color="text.secondary" noWrap component="p">{t("platform.sidebar.activeSession")}</Typography>
              <Tooltip title={user?.username || user?.email || ""}><Typography fontSize={16} fontWeight={600} noWrap>{user?.username || user?.email || t("platform.sidebar.user")}</Typography></Tooltip>
            </Box>
            <Typography variant="caption" color="primary.main" sx={{ fontFamily: theme.custom.monoFont, fontSize: 10, px: 0.75, py: 0.25, border: "1px solid", borderColor: "divider", borderRadius: "4px" }}>{normalizedRole.toUpperCase()}</Typography>
          </Box>
        </Box>}
      </Box>
      <ProSidebar collapsed={isCollapsed}>
        <Menu iconShape="square">
          <Box className="navigation-items" sx={{ "& .MuiTypography-body1": { fontSize: 17, fontWeight: 500 } }}>
            {visibleModules.map((module) => (
              <Item
                key={module.key}
                title={module.labelKey ? t(module.labelKey, module.label) : module.label}
                to={module.to}
                icon={module.icon}
                isCollapsed={isCollapsed}
                onNavigate={onNavigate}
                badge={module.key === "incidents" && criticalCount ? criticalCount : null}
                bottom={module.key === "system"}
              />
            ))}
          </Box>
        </Menu>
      </ProSidebar>
    </Box>
  );
};

export default Sidebar;
